import {
  ChartBarIcon,
  DocumentArrowDownIcon,
  DocumentTextIcon,
  TableCellsIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import { Vehicle } from '../../types';

interface ExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: Vehicle[];
  dataType: 'vehicles' | 'pricing' | 'analytics';
  onExport: (
    format: 'csv' | 'excel' | 'pdf',
    selectedFields: string[],
    includeCharts?: boolean
  ) => void;
  loading?: boolean;
}

const ExportModal: React.FC<ExportModalProps> = ({
  isOpen,
  onClose,
  data,
  dataType,
  onExport,
  loading = false,
}) => {
  const [selectedFormat, setSelectedFormat] = useState<'csv' | 'excel' | 'pdf'>('csv');
  const [selectedFields, setSelectedFields] = useState<string[]>([]);
  const [includeCharts, setIncludeCharts] = useState(false);
  const [exportName, setExportName] = useState('');

  // Define available fields based on data type
  const getAvailableFields = () => {
    switch (dataType) {
      case 'vehicles':
        return [
          { key: 'vin', label: 'VIN', required: true },
          { key: 'stock_number', label: 'Stock Number' },
          { key: 'year', label: 'Year', required: true },
          { key: 'make', label: 'Make', required: true },
          { key: 'model', label: 'Model', required: true },
          { key: 'trim', label: 'Trim' },
          { key: 'mileage', label: 'Mileage' },
          { key: 'asking_price', label: 'Asking Price' },
          { key: 'acquisition_cost', label: 'Acquisition Cost' },
          { key: 'status', label: 'Status', required: true },
          { key: 'location', label: 'Location' },
          { key: 'exterior_color', label: 'Exterior Color' },
          { key: 'interior_color', label: 'Interior Color' },
          { key: 'created_at', label: 'Date Added' },
          { key: 'updated_at', label: 'Last Updated' },
        ];
      case 'pricing':
        return [
          { key: 'vehicle_id', label: 'Vehicle ID', required: true },
          { key: 'year', label: 'Year' },
          { key: 'make', label: 'Make' },
          { key: 'model', label: 'Model' },
          { key: 'asking_price', label: 'Asking Price' },
          { key: 'wholesale_value', label: 'Wholesale Value' },
          { key: 'retail_value', label: 'Retail Value' },
          { key: 'profit_margin', label: 'Profit Margin' },
          { key: 'days_on_lot', label: 'Days on Lot' },
          { key: 'status', label: 'Status' },
        ];
      case 'analytics':
        return [
          { key: 'period', label: 'Period' },
          { key: 'total_sales', label: 'Total Sales' },
          { key: 'total_revenue', label: 'Total Revenue' },
          { key: 'average_profit', label: 'Average Profit' },
          { key: 'top_performing_make', label: 'Top Make' },
          { key: 'inventory_turnover', label: 'Inventory Turnover' },
          { key: 'days_on_lot_avg', label: 'Avg Days on Lot' },
        ];
      default:
        return [];
    }
  };

  const availableFields = getAvailableFields();

  // Set default selected fields (required + some common ones)
  React.useEffect(() => {
    if (isOpen && selectedFields.length === 0) {
      const defaultFields = availableFields
        .filter((field) => field.required)
        .map((field) => field.key);
      setSelectedFields(defaultFields);
    }
  }, [isOpen, availableFields, selectedFields.length]);

  const handleFieldToggle = (fieldKey: string) => {
    setSelectedFields((prev) =>
      prev.includes(fieldKey) ? prev.filter((f) => f !== fieldKey) : [...prev, fieldKey]
    );
  };

  const handleSelectAll = () => {
    setSelectedFields(availableFields.map((field) => field.key));
  };

  const handleSelectRequired = () => {
    setSelectedFields(availableFields.filter((field) => field.required).map((field) => field.key));
  };

  const handleExport = () => {
    if (selectedFields.length === 0) {
      toast.error('Please select at least one field to export.');
      return;
    }

    onExport(selectedFormat, selectedFields, includeCharts);
    onClose();

    // Reset form
    setSelectedFormat('csv');
    setSelectedFields([]);
    setIncludeCharts(false);
    setExportName('');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <DocumentArrowDownIcon className="h-6 w-6 text-gray-400 mr-3" />
            <h2 className="text-lg font-medium text-gray-900">
              Export {dataType.charAt(0).toUpperCase() + dataType.slice(1)} Data
            </h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Export Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Export Name (Optional)
            </label>
            <input
              type="text"
              value={exportName}
              onChange={(e) => setExportName(e.target.value)}
              placeholder={`${dataType}_export_${new Date().toISOString().split('T')[0]}`}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Format Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">Export Format</label>
            <div className="grid grid-cols-3 gap-3">
              <button
                onClick={() => setSelectedFormat('csv')}
                className={`flex items-center justify-center p-4 border rounded-lg ${
                  selectedFormat === 'csv'
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <TableCellsIcon className="h-6 w-6 mr-2" />
                <div className="text-left">
                  <div className="font-medium">CSV</div>
                  <div className="text-xs text-gray-500">Spreadsheet</div>
                </div>
              </button>
              <button
                onClick={() => setSelectedFormat('excel')}
                className={`flex items-center justify-center p-4 border rounded-lg ${
                  selectedFormat === 'excel'
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <DocumentTextIcon className="h-6 w-6 mr-2" />
                <div className="text-left">
                  <div className="font-medium">Excel</div>
                  <div className="text-xs text-gray-500">Formatted</div>
                </div>
              </button>
              <button
                onClick={() => setSelectedFormat('pdf')}
                className={`flex items-center justify-center p-4 border rounded-lg ${
                  selectedFormat === 'pdf'
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <DocumentTextIcon className="h-6 w-6 mr-2" />
                <div className="text-left">
                  <div className="font-medium">PDF</div>
                  <div className="text-xs text-gray-500">Report</div>
                </div>
              </button>
            </div>
          </div>

          {/* Field Selection */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="block text-sm font-medium text-gray-700">
                Select Fields ({selectedFields.length} selected)
              </label>
              <div className="flex gap-2">
                <button
                  onClick={handleSelectAll}
                  className="text-xs text-blue-600 hover:text-blue-800"
                >
                  Select All
                </button>
                <button
                  onClick={handleSelectRequired}
                  className="text-xs text-blue-600 hover:text-blue-800"
                >
                  Required Only
                </button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto border border-gray-300 rounded-md p-3">
              {availableFields.map((field) => (
                <label key={field.key} className="flex items-center">
                  <input
                    type="checkbox"
                    checked={selectedFields.includes(field.key)}
                    onChange={() => handleFieldToggle(field.key)}
                    disabled={field.required}
                    className="mr-2"
                  />
                  <span className={`text-sm ${field.required ? 'font-medium' : ''}`}>
                    {field.label}
                    {field.required && <span className="text-red-500 ml-1">*</span>}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Include Charts (for PDF exports) */}
          {selectedFormat === 'pdf' && dataType !== 'vehicles' && (
            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={includeCharts}
                  onChange={(e) => setIncludeCharts(e.target.checked)}
                  className="mr-2"
                />
                <span className="text-sm">
                  <ChartBarIcon className="h-4 w-4 inline mr-1" />
                  Include charts and visualizations
                </span>
              </label>
            </div>
          )}

          {/* Export Summary */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="text-sm font-medium text-gray-900 mb-2">Export Summary</h4>
            <div className="text-sm text-gray-600 space-y-1">
              <div>Records: {data.length}</div>
              <div>Fields: {selectedFields.length}</div>
              <div>Format: {selectedFormat.toUpperCase()}</div>
              {includeCharts && <div>Includes: Charts & Visualizations</div>}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
          <div className="text-sm text-gray-600">{data.length} records will be exported</div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleExport}
              disabled={loading || selectedFields.length === 0}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Exporting...' : 'Export Data'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExportModal;
